import { useMemo } from 'react';
import type { TimeSeriesPoint } from '../AggregatedMetric/aggregation';
import type { RelativePerformanceNormalizedSeries, RelativePerformanceTableRow } from './Comparison.types';

interface NormalizeForDateRangeResult {
  normalizedSeries: RelativePerformanceNormalizedSeries[];
  absoluteSeries: RelativePerformanceNormalizedSeries[];
  tableRows: RelativePerformanceTableRow[];
  excludedCmAssetIds: string[];
}

interface UseComparisonNormalizationParams {
  dataByAsset: ReadonlyMap<string, TimeSeriesPoint[]>;
  metric: string;
  startDate: string;
  endDate: string;
  selectedCmAssetIds: string[];
  talosSymbolByCmAssetId: ReadonlyMap<string, string>;
}

// Exported for direct unit testing.
export function normalizeForDateRange(
  dataByAsset: ReadonlyMap<string, TimeSeriesPoint[]>,
  metric: string,
  startDate: string,
  endDate: string,
  selectedCmAssetIds: string[],
  talosSymbolByCmAssetId: ReadonlyMap<string, string>
): NormalizeForDateRangeResult {
  const startTs = new Date(startDate + 'T00:00:00.000Z').getTime();
  const endTs = new Date(endDate + 'T00:00:00.000Z').getTime();

  const normalizedSeries: RelativePerformanceNormalizedSeries[] = [];
  const absoluteSeries: RelativePerformanceNormalizedSeries[] = [];
  const tableRows: RelativePerformanceTableRow[] = [];
  const excludedCmAssetIds: string[] = [];

  for (const cmAssetId of selectedCmAssetIds) {
    const talosSymbol = talosSymbolByCmAssetId.get(cmAssetId) ?? cmAssetId;
    const series = dataByAsset.get(cmAssetId);
    const inRange = series?.filter(([ts]) => ts >= startTs && ts <= endTs) ?? [];

    if (inRange.length === 0) {
      excludedCmAssetIds.push(cmAssetId);
      tableRows.push({
        cmAssetId,
        talosSymbol,
        metric,
        startValue: null,
        endValue: null,
        changePercent: null,
        minPercent: null,
        maxPercent: null,
        hasData: false,
        sparklineData: [],
      });
      continue;
    }

    // Absolute mode needs no normalization — push raw series regardless of anchor value.
    absoluteSeries.push({ cmAssetId, talosSymbol, data: inRange });

    // First in-range point is the anchor. Zero anchor means we cannot normalize (division by zero).
    const anchorValue = inRange[0][1];
    const endValue = inRange.at(-1)![1];

    if (anchorValue === 0) {
      excludedCmAssetIds.push(cmAssetId);
      tableRows.push({
        cmAssetId,
        talosSymbol,
        metric,
        startValue: anchorValue,
        endValue,
        changePercent: null,
        minPercent: null,
        maxPercent: null,
        hasData: false,
        sparklineData: [],
      });
      continue;
    }
    const normalizedData: [number, number][] = inRange.map(([ts, value]) => [ts, (value / anchorValue - 1) * 100]);

    let minPercent = Infinity;
    let maxPercent = -Infinity;
    for (const [, pct] of normalizedData) {
      if (pct < minPercent) {
        minPercent = pct;
      }
      if (pct > maxPercent) {
        maxPercent = pct;
      }
    }

    normalizedSeries.push({ cmAssetId, talosSymbol, data: normalizedData });
    tableRows.push({
      cmAssetId,
      talosSymbol,
      metric,
      startValue: anchorValue,
      endValue,
      changePercent: (endValue / anchorValue - 1) * 100,
      minPercent,
      maxPercent,
      hasData: true,
      sparklineData: normalizedData.map(([, v]) => v),
    });
  }

  return { normalizedSeries, absoluteSeries, tableRows, excludedCmAssetIds };
}

export function useComparisonNormalization({
  dataByAsset,
  metric,
  startDate,
  endDate,
  selectedCmAssetIds,
  talosSymbolByCmAssetId,
}: UseComparisonNormalizationParams): NormalizeForDateRangeResult {
  return useMemo(
    () => normalizeForDateRange(dataByAsset, metric, startDate, endDate, selectedCmAssetIds, talosSymbolByCmAssetId),
    [dataByAsset, metric, startDate, endDate, selectedCmAssetIds, talosSymbolByCmAssetId]
  );
}
